// src/pages/AdminLiveMap.jsx
import React, { useState, useEffect } from "react";
import {
  Map,
  Marker,
  Popup,
  NavigationControl,
  FullscreenControl,
} from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../services/firebase";
import Layout from "../component/Layout";
import { useOperator } from "../contexts/OperaotrContext";

const AdminLiveMap = () => {
  const [jreLocations, setJreLocations] = useState({});
  const [viewport, setViewport] = useState({
    latitude: 21.0,
    longitude: 86.3,
    zoom: 7,
  });
  const [selectedJre, setSelectedJre] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const { Operator } = useOperator();

  useEffect(() => {
    setIsLoading(true);
    console.log("Starting Firebase subscription...");

    const q = query(
      collection(db, "jreStatus"),
      where("operatorId", "==", Operator.OperatorId),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log("Snapshot received:", snapshot.size, "documents");

        const updatedLocations = {};
        let hasValidLocations = false;

        snapshot.forEach((doc) => {
          const data = doc.data();
          console.log("Document data:", doc.id, data);

          // Check if location data exists and is valid
          if (
            data.location &&
            typeof data.location.latitude === "number" &&
            typeof data.location.longitude === "number"
          ) {
            updatedLocations[doc.id] = {
              id: doc.id,
              position: {
                lat: data.location.latitude,
                lng: data.location.longitude,
              },
              lastUpdate: data.updatedAt?.toDate?.() || new Date(),
              status: data.status || "active",
              vehicleNumber: data.vehicleNumber || "N/A",
              speed: data.speed || 0,
              heading: data.heading || 0,
              isOnline: data.isOnline || false,
              currentValue: data.currentValue || 0,
            };
            hasValidLocations = true;
            console.log("Added location for JRE:", doc.id, data.location);
          } else {
            console.log(
              "Invalid or missing location data for JRE:",
              doc.id,
              data.location,
            );
          }
        });

        setJreLocations(updatedLocations);
        setLastUpdated(new Date());
        setIsLoading(false);

        if (!hasValidLocations && snapshot.size > 0) {
          console.warn(
            "No valid location data found in documents. Check your Firebase data structure.",
          );
        }
      },
      (error) => {
        console.error("Error fetching JRE locations:", error);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  // Fit bounds to show all markers
  const fitToMarkers = () => {
    if (Object.keys(jreLocations).length === 0) return;

    const coordinates = Object.values(jreLocations).map((jre) => [
      jre.position.lng,
      jre.position.lat,
    ]);

    const lngs = coordinates.map((coord) => coord[0]);
    const lats = coordinates.map((coord) => coord[1]);

    const newViewport = {
      longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2,
      latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
      zoom: 10,
    };

    setViewport(newViewport);
  };

  // Get marker color based on isOnline status
  const getMarkerColor = (isOnline) => {
    return isOnline ? "#22c55e" : "#ef4444";
  };

  // Handle input changes

  // Professional map style
  const MAP_STYLE =
    "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json";

  return (
    <Layout title="JRE Live">
      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.titleSection}>
              <h1 style={styles.title}>Live JRE Tracking</h1>
              <p style={styles.subtitle}>Real-time vehicle monitoring</p>
            </div>
            <div style={styles.controls}>
              <div style={styles.stats}>
                <div style={styles.statCard}>
                  <span style={styles.statNumber}>
                    {Object.keys(jreLocations).length}
                  </span>
                  <span style={styles.statLabel}>Online Vehicles</span>
                </div>
                <div style={styles.statCard}>
                  <span style={styles.statNumber}>
                    {lastUpdated.toLocaleTimeString()}
                  </span>
                  <span style={styles.statLabel}>Last Updated</span>
                </div>
              </div>
              <button
                onClick={fitToMarkers}
                style={styles.fitButton}
                disabled={Object.keys(jreLocations).length === 0}
              >
                <span style={styles.buttonIcon}>📍</span>
                Fit to Markers
              </button>
            </div>
          </div>
        </header>

        <div style={styles.mapContainer}>
          {isLoading && (
            <div style={styles.loadingOverlay}>
              <div style={styles.loadingSpinner}>
                <div style={styles.spinner}></div>
              </div>
              <p style={styles.loadingText}>Loading vehicle locations...</p>
            </div>
          )}

          <Map
            {...viewport}
            onMove={(evt) => setViewport(evt.viewState)}
            style={styles.map}
            mapStyle={MAP_STYLE}
            attributionControl={true}
          >
            <NavigationControl position="top-left" />
            <FullscreenControl position="top-left" />

            {Object.values(jreLocations).map((jre) => (
              <Marker
                key={jre.id}
                longitude={jre.position.lng}
                latitude={jre.position.lat}
                onClick={() => {
                  setSelectedJre(jre);
                }}
              >
                <div
                  style={{
                    ...styles.marker,
                    backgroundColor: getMarkerColor(jre.isOnline),
                  }}
                >
                  <div style={styles.markerInner}>
                    <span style={styles.markerIcon}>🏍️</span>
                  </div>
                  <div style={styles.markerPulse}></div>
                </div>
              </Marker>
            ))}

            {selectedJre && (
              <Popup
                longitude={selectedJre.position.lng}
                latitude={selectedJre.position.lat}
                onClose={() => {
                  setSelectedJre(null);
                }}
                anchor="bottom"
                closeButton={true}
                closeOnClick={false}
                style={styles.popup}
              >
                <div style={styles.popupContent}>
                  <div style={styles.popupHeader}>
                    <h4 style={styles.popupTitle}>
                      Vehicle {selectedJre.vehicleNumber}
                    </h4>
                    <span
                      style={{
                        ...styles.statusBadge,
                        backgroundColor: getMarkerColor(selectedJre.isOnline),
                      }}
                    >
                      {selectedJre.isOnline ? "ONLINE" : "OFFLINE"}
                    </span>
                  </div>

                  <div style={styles.popupDetails}>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>JRE ID:</span>
                      <span style={styles.detailValue}>{selectedJre.id}</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Speed:</span>
                      <span style={styles.detailValue}>
                        {selectedJre.speed} km/h
                      </span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Last Update:</span>
                      <span style={styles.detailValue}>
                        {selectedJre.lastUpdate.toLocaleTimeString()}
                      </span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Current Hold:</span>
                      <span style={styles.detailValue}>
                        {selectedJre.currentValue?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </Popup>
            )}
          </Map>
        </div>

        {/* Legend */}
        <div style={styles.legend}>
          <div style={styles.legendTitle}>Status Legend</div>
          <div style={styles.legendItems}>
            <div style={styles.legendItem}>
              <div
                style={{ ...styles.legendColor, backgroundColor: "#22c55e" }}
              ></div>
              <span>Online</span>
            </div>
            <div style={styles.legendItem}>
              <div
                style={{ ...styles.legendColor, backgroundColor: "#ef4444" }}
              ></div>
              <span>Offline</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const styles = {
  container: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#f8fafc",
  },
  header: {
    backgroundColor: "white",
    color: "#1e293b",
    padding: "1.5rem 2rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    zIndex: 10,
    borderBottom: "1px solid #e2e8f0",
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "1rem",
    maxWidth: "1400px",
    margin: "0 auto",
  },
  titleSection: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  },
  title: {
    margin: 0,
    fontSize: "1.75rem",
    fontWeight: "700",
    color: "#1e293b",
  },
  subtitle: {
    margin: 0,
    fontSize: "0.9rem",
    color: "#64748b",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: "1.5rem",
    flexWrap: "wrap",
  },
  stats: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
  statCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0.75rem 1rem",
    backgroundColor: "#f8fafc",
    borderRadius: "8px",
    minWidth: "100px",
  },
  statNumber: {
    fontSize: "1.25rem",
    fontWeight: "700",
    color: "#1e293b",
  },
  statLabel: {
    fontSize: "0.75rem",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  fitButton: {
    padding: "0.75rem 1.25rem",
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 4px rgba(59, 130, 246, 0.3)",
  },
  buttonIcon: {
    fontSize: "1rem",
  },
  mapContainer: {
    flex: 1,
    position: "relative",
    backgroundColor: "#e2e8f0",
  },
  map: {
    width: "100%",
    height: "100%",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255,255,255,0.95)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 5,
  },
  loadingSpinner: {
    marginBottom: "1rem",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e2e8f0",
    borderTop: "4px solid #3b82f6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    margin: 0,
    color: "#64748b",
    fontSize: "1rem",
  },
  marker: {
    position: "relative",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  },
  markerInner: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  markerIcon: {
    fontSize: "12px",
  },
  markerPulse: {
    position: "absolute",
    top: "-8px",
    left: "-8px",
    right: "-8px",
    bottom: "-8px",
    borderRadius: "50%",
    backgroundColor: "currentColor",
    opacity: 0.4,
    animation: "pulse 2s infinite",
  },
  popup: {
    maxWidth: "350px",
  },
  popupContent: {
    padding: "0.75rem",
  },
  popupHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.75rem",
  },
  popupTitle: {
    margin: 0,
    fontSize: "1rem",
    color: "#1e293b",
  },
  statusBadge: {
    padding: "0.25rem 0.5rem",
    borderRadius: "12px",
    fontSize: "0.7rem",
    fontWeight: "600",
    color: "white",
  },
  popupDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  detailItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  detailLabel: {
    fontSize: "0.8rem",
    color: "#64748b",
    fontWeight: "500",
  },
  detailValue: {
    fontSize: "0.8rem",
    color: "#1e293b",
    fontWeight: "600",
  },

  // Edit section styles
  editSection: {
    paddingTop: "0.75rem",
    borderTop: "1px solid #e2e8f0",
    marginTop: "0.5rem",
  },
  editTitle: {
    fontSize: "0.9rem",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "0.5rem",
  },
  editForm: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  },
  inputLabel: {
    fontSize: "0.75rem",
    color: "#64748b",
    fontWeight: "500",
  },
  input: {
    padding: "0.5rem",
    border: "1px solid #cbd5e1",
    borderRadius: "4px",
    fontSize: "0.875rem",
    width: "100%",
  },
  editButtons: {
    display: "flex",
    gap: "0.5rem",
    marginTop: "0.25rem",
  },
  saveButton: {
    flex: 1,
    padding: "0.5rem",
    backgroundColor: "#22c55e",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "600",
  },
  cancelButton: {
    flex: 1,
    padding: "0.5rem",
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "1px solid #cbd5e1",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "600",
  },
  valueDisplay: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  valueRow: {
    display: "flex",
    justifyContent: "space-between",
  },
  valueLabel: {
    fontSize: "0.8rem",
    color: "#64748b",
    fontWeight: "500",
  },
  valueText: {
    fontSize: "0.8rem",
    color: "#1e293b",
    fontWeight: "600",
  },
  editButton: {
    marginTop: "0.5rem",
    padding: "0.5rem",
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "600",
    textAlign: "center",
  },

  legend: {
    position: "absolute",
    bottom: "20px",
    left: "20px",
    backgroundColor: "white",
    padding: "1rem",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: 5,
  },
  legendTitle: {
    fontSize: "0.9rem",
    fontWeight: "600",
    marginBottom: "0.5rem",
    color: "#1e293b",
  },
  legendItems: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  legendColor: {
    width: "16px",
    height: "16px",
    borderRadius: "4px",
  },
};

// Add CSS animations
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  document.head.appendChild(style);
  const sheet = style.sheet;

  sheet.insertRule(
    `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `,
    sheet.cssRules.length,
  );

  sheet.insertRule(
    `
    @keyframes pulse {
      0% { transform: scale(1); opacity: 0.4; }
      50% { transform: scale(1.2); opacity: 0.2; }
      100% { transform: scale(1); opacity: 0.4; }
    }
  `,
    sheet.cssRules.length,
  );
}

export default AdminLiveMap;
